import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useCreateFieldMutation, useUpdateFieldMutation } from '../../../store/api/apiSlice';
import type { DynamicField, CreateFieldRequest } from '../../../types';
import { RootState, useAppSelector } from '../../../store';
import { selectFieldById } from '../../../store/selectors';

interface EditFieldModalProps {
  open: boolean;
  onClose: () => void;
  onError: (error: string) => void;
  fieldId: string;
}

const defaultPromptInstructions = "Focus on systematic assessment using this framework. Be thorough in gathering relevant information and pay attention to key clinical indicators. Guide the conversation to cover essential assessment categories while maintaining a natural patient interaction.";

export const EditFieldModal: React.FC<EditFieldModalProps> = ({
  open,
  onClose,
  onError,
  fieldId
}) => {

  const field = useAppSelector((state: RootState) => selectFieldById(state, fieldId));
  const [formData, setFormData] = useState<CreateFieldRequest>({
    fieldName: '',
    description: '',
    promptInstructions: defaultPromptInstructions
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [updateField, { isLoading }] = useUpdateFieldMutation();

  // Reset form when modal opens
  React.useEffect(() => {
    if (open) {
      setFormData({
        fieldName: field?.field_name || '',
        description: field?.description || '',
        promptInstructions: field?.prompt_instructions || defaultPromptInstructions
      });
      setErrors({});
    }
  }, [open]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.fieldName.trim()) {
      newErrors.fieldName = 'Field name is required';
    } else if (formData.fieldName.length < 3) {
      newErrors.fieldName = 'Field name must be at least 3 characters';
    } else if (formData.fieldName.length > 100) {
      newErrors.fieldName = 'Field name cannot exceed 100 characters';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.length < 10) {
      newErrors.description = 'Description must be at least 10 characters';
    } else if (formData.description.length > 500) {
      newErrors.description = 'Description cannot exceed 500 characters';
    }

    if (formData.promptInstructions && formData.promptInstructions.length > 2000) {
      newErrors.promptInstructions = 'Prompt instructions cannot exceed 2000 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const result = await updateField({
        fieldId: fieldId,
        updates: formData
      }).unwrap();
      onClose();
    } catch (error: any) {
      console.error('Failed to create field:', error);
      onError(error.message || 'Failed to create field');
    }
  };

  const handleChange = (field: keyof CreateFieldRequest) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1
      }}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
          Edit Clinical Field
        </Typography>
        <IconButton 
          onClick={handleClose} 
          disabled={isLoading}
          sx={{ color: 'text.primary' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" sx={{ color: 'text.primary', mb: 3 }}>
            Edit the clinical assessment field with its own framework and prompt instructions.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Field Name */}
            <TextField
              label="Field Name"
              value={formData.fieldName}
              onChange={handleChange('fieldName')}
              error={!!errors.fieldName}
              helperText={errors.fieldName || 'e.g., "Pain Assessment (PQRST)", "Cardiovascular History"'}
              required
              fullWidth
              disabled={isLoading}
              placeholder="Enter the clinical field name"
            />

            {/* Description */}
            <TextField
              label="Description"
              value={formData.description}
              onChange={handleChange('description')}
              error={!!errors.description}
              helperText={errors.description || 'Describe the clinical framework or assessment methodology'}
              required
              fullWidth
              multiline
              rows={3}
              disabled={isLoading}
              placeholder="e.g., Comprehensive pain evaluation using PQRST methodology: Provocation, Quality, Region/Radiation, Severity, Timing"
            />

            {/* Prompt Instructions */}
            <TextField
              label="Prompt Instructions"
              value={formData.promptInstructions}
              onChange={handleChange('promptInstructions')}
              error={!!errors.promptInstructions}
              helperText={errors.promptInstructions || 'Default instructions for AI when processing cases in this field'}
              fullWidth
              multiline
              rows={4}
              disabled={isLoading}
              placeholder="Enter specific instructions for this clinical field..."
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button 
            onClick={handleClose} 
            disabled={isLoading}
            sx={{ 
              color: '#FFFFFF',
              textTransform: 'none',
              backgroundColor: 'error.main',
              '&:hover': {
                backgroundColor: 'error.main',
              }
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            sx={{
              backgroundColor: 'primary.main',
              color: 'text.primary',
              fontWeight: 600,
              textTransform: 'none',
              minWidth: 120,
              '&:hover': {
                backgroundColor: 'primary.dark',
              }
            }}
          >
            {isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Update Field'
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};